import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TablePagination } from '../../../components/common/TablePagination';
import { FormDatePicker } from '../../../components/Form/FormDatePicker';
import { useCasePermissions } from '../../../hooks/UseCasePermission';
import {
  deletePloForm,
  fetchPloApplications,
} from '../../../services/PloFiveService';
import type { User } from '../../../types/user';
import { ploFiveTableFilters } from '../../../hooks/plo/PLOFiveTableFilter';

export function PLOFormFiveTable() {
  const navigate = useNavigate();

  const itemsPerPage = 10;

  const filters = ploFiveTableFilters();

  const [currentUser] = useState<User | null>(() => {
    if (typeof window === 'undefined') return null;
    try {
      return JSON.parse(localStorage.getItem('user') || 'null');
    } catch {
      return null;
    }
  });

  useCasePermissions(currentUser);

  const checkIsOriginUser = (app: any): boolean => {
    if (!currentUser) return false;

    const originOfficeId = app.originOfficeId || app.originOffice?.id || app.document?.originOfficeId || app.officeId;
    if (currentUser.officeId && originOfficeId && currentUser.officeId === originOfficeId) {
      return true;
    }

    const createdById = app.createdById || app.createdBy?.id || app.document?.createdById;
    if (currentUser.id && createdById && currentUser.id === createdById) {
      return true;
    }

    return false;
  };

  const { data, isLoading, isError, error, isPlaceholderData, refetch, isFetching } = useQuery({
    queryKey: [
      'ploFormFive',
      filters.debouncedSearch,
      filters.debouncedCourt,
      filters.debouncedParty,
      filters.selectedStatus,
      filters.ptiFirstDate,
      filters.ptiSecondDate,
      filters.currentPage,
    ],
    queryFn: () =>
      fetchPloApplications({
        searchTerm: filters.debouncedSearch,
        courtSearch: filters.debouncedCourt,
        partySearch: filters.debouncedParty,
        selectedStatus: filters.selectedStatus,
        ptiFirstDate: filters.ptiFirstDate,
        ptiSecondDate: filters.ptiSecondDate,
        currentPage: filters.currentPage,
        itemsPerPage,
      }),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  // Safely extract applications array handling both direct array & wrapped { data: [...] } formats
  const rawList = Array.isArray(data) ? data : data?.data;
  const applications = Array.isArray(rawList) ? rawList : [];
  const totalItems = data?.totalItems || applications.length || 0;
  const totalPages = Math.ceil(totalItems / itemsPerPage) || 1;

  const handleDelete = async (id?: string) => {
    if (!id) return;

    const confirmed = window.confirm('ဤမှတ်တမ်းကို ဖျက်ရန် သေချာပါသလား။');
    if (!confirmed) return;

    try {
      await deletePloForm(id);
      await refetch();
    } catch (err: any) {
      console.error('Delete failed:', err);
      alert(err?.message || 'ဖျက်ခြင်း မအောင်မြင်ပါ။');
    }
  };

  return (
    <div className="space-y-4 bg-white p-4 md:p-6 rounded-2xl border border-slate-200 shadow-sm">
      {/* Table Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-900 tracking-tight">
            PLOပုံစံ (၅) စာရင်းများ
          </h2>
        </div>

        {/* Action Buttons Group */}
        <div className="flex items-center gap-2 self-start sm:self-center shrink-0">
          <button
            type="button"
            onClick={() => refetch()}
            disabled={isFetching}
            className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm hover:bg-slate-50 transition-colors disabled:opacity-50"
            title="ဒေတာများ နောက်ဆုံး Data များ ရယူရန် "
          >
            <svg
              className={`h-3.5 w-3.5 ${isFetching ? 'animate-spin text-blue-600' : 'text-slate-500'}`}
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99"
              />
            </svg>
            နောက်ဆုံး Data များ ရယူရန် 
          </button>

          <button
            type="button"
            onClick={() => navigate('/plo-form-five/create')}
            className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-blue-600 px-3.5 py-2 text-xs font-semibold text-white shadow-sm hover:bg-blue-500 transition-colors shrink-0"
          >
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
            </svg>
            ဖောင်အသစ်ထည့်ရန်
          </button>
        </div>
      </div>

      {/* Filter Controls Bar */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2 items-end bg-slate-50/60 p-3 rounded-xl border border-slate-100">
        <div>
          <label className="block text-[11px] font-medium text-slate-500 mb-1">အထွေထွေ ရှာဖွေရန်</label>
          <input
            type="text"
            placeholder="စာရွက်စာတမ်းနံပါတ်/အမှုနံပါတ်/ပုဒ်မ..."
            value={filters.searchTerm}
            onChange={(e) => {
              filters.setSearchTerm(e.target.value);
              filters.setCurrentPage(1);
            }}
            className="w-full rounded-md border border-slate-200 bg-white py-1.5 px-2.5 text-xs text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-[11px] font-medium text-slate-500 mb-1">
            တရားရုံး<br />အမည်
          </label>
          <input
            type="text"
            placeholder="တရားရုံး အမည်..."
            value={filters.courtSearch}
            onChange={(e) => {
              filters.setCourtSearch(e.target.value);
              filters.setCurrentPage(1);
            }}
            className="w-full rounded-md border border-slate-200 bg-white py-1.5 px-2.5 text-xs text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-[11px] font-medium text-slate-500 mb-1">နစ်နာသူ / သက်သေအမည်</label>
          <input
            type="text"
            placeholder="နစ်နာသူ / သက်သေအမည်..."
            value={filters.partySearch}
            onChange={(e) => {
              filters.setPartySearch(e.target.value);
              filters.setCurrentPage(1);
            }}
            className="w-full rounded-md border border-slate-200 bg-white py-1.5 px-2.5 text-xs text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-[11px] font-medium text-slate-500 mb-1">PTI ပထမအကြိမ် နေ့စွဲ</label>
          <FormDatePicker
            value={filters.ptiFirstDate || ''}
            onChange={(e) => {
              filters.setPtiFirstDate?.(e.target.value);
              filters.setCurrentPage(1);
            }}
            name="ptiFirstDate"
            className="w-full"
          />
        </div>

        <div className="flex items-center gap-1.5">
          <div className="flex-1">
            <label className="block text-[11px] font-medium text-slate-500 mb-1">PTI ဒုတိယအကြိမ် နေ့စွဲ</label>
            <FormDatePicker
              value={filters.ptiSecondDate || ''}
              onChange={(e) => {
                filters.setPtiSecondDate?.(e.target.value);
                filters.setCurrentPage(1);
              }}
              name="ptiSecondDate"
              className="w-full"
            />
          </div>

          {filters.hasActiveFilters && (
            <button
              type="button"
              onClick={filters.handleClearFilters}
              title="စစ်ထုတ်မှုများ ဖျက်ရန်"
              className="mt-5 shrink-0 inline-flex items-center justify-center text-xs font-medium text-rose-600 hover:bg-rose-50 border border-rose-200 rounded-md py-1.5 px-2 bg-white transition-colors"
            >
              🔄
            </button>
          )}
        </div>
      </div>

      {/* Table Section */}
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm relative">
        {isPlaceholderData && (
          <div className="absolute inset-0 bg-white/40 backdrop-blur-[1px] flex items-center justify-center z-10">
            <span className="text-xs bg-slate-800 text-white px-2.5 py-1 rounded-full shadow-sm">
              နောက်ဆုံးပေါ်အချက်အလက် ရယူနေပါသည်...
            </span>
          </div>
        )}

        <div className="w-full overflow-x-auto border border-black">
          <table className="w-full text-left border-collapse text-[11px] min-w-[1200px]">
            <thead>
              {/* Row ခေါင်းစဉ်ကြီးများ */}
              <tr className="bg-slate-50 border-b border-black text-center align-middle font-bold">
                <th rowSpan={2} className="border-r border-black p-2 w-10">စဉ်</th>
                <th colSpan={4} className="border-r border-black p-1 bg-slate-100/70">နစ်နာသူ / သက်သေအချက်အလက်</th>
                <th colSpan={2} className="border-r border-black p-1 bg-slate-50">PTI ပြုလုပ်သည့်နေ့စွဲ</th>
                <th colSpan={2} className="border-r border-black p-1 bg-slate-100/70">တွေ့ဆုံမေးမြန်းသူအချက်အလက်</th>
                <th colSpan={3} className="border-r border-black p-1 bg-slate-50">စီရင်ချက်အမိန့်</th>
                <th colSpan={2} className="border-r border-black p-1 bg-slate-100/70">အယူခံ / ပြင်ဆင်</th>
                <th rowSpan={2} className="border-r border-black p-1 w-20">မှတ်ချက်</th>
                <th rowSpan={2} className="p-1 w-24 font-sans text-gray-500">Action</th>
              </tr>
              {/* Sub Headers ခေါင်းစဉ်ခွဲများ */}
              <tr className="bg-slate-50 border-b border-black text-center align-middle text-[10px] font-bold">
                {/* နစ်နာသူ / သက်သေအချက်အလက် */}
                <th className="border-r border-black p-1 w-12">၁။ကျား<br/>၂။မ</th>
                <th className="border-r border-black p-1 w-16">သက်သေ၏<br/>အသက်</th>
                <th className="border-r border-black p-1 w-24">နစ်နာသူ / သက်သေအမည်</th>
                <th className="border-r border-black p-1 w-20">သက်သေကြားနာ<br/>မည့်နေ့</th>
                {/* PTI ပြုလုပ်သည့်နေ့စွဲ */}
                <th className="border-r border-black p-1 w-20">PTI ပထမအကြိမ်<br/>ပြုလုပ်သည့်နေ့</th>
                <th className="border-r border-black p-1 w-20">PTI ဒုတိယအကြိမ်<br/>ပြုလုပ်သည့်နေ့</th>
                {/* တွေ့ဆုံမေးမြန်းသူအချက်အလက် */}
                <th className="border-r border-black p-1 w-24">တွေ့ဆုံမေးမြန်းသူ<br/>အမည်</th>
                <th className="border-r border-black p-1 w-28 text-left text-[9px] px-1 font-normal">
                  <b>၁။</b>ခရိုင်ဥပဒေအရာရှိ <b>၂။</b>ဒုခရိုင်<br/>
                  <b>၃။</b>မြို့နယ် <b>၄။</b>ဒုမြို့နယ်<br/>
                  <b>၅။</b>ဦးစီးအရာရှိ <b>၆။</b>အခြား
                </th>
                {/* စီရင်ချက်အမိန့် */}
                <th className="border-r border-black p-1 w-24 text-[9px] font-normal">
                  <b>၁။</b>တရားသေလွတ်သည်<br/>
                  <b>၂။</b>တရားရှင်လွတ်သည်<br/>
                  <b>၃။</b>တရားစွဲအမိန့်
                </th>
                <th className="border-r border-black p-1 w-16">အမိန့်ချသည့်<br/>ရက်စွဲ</th>
                <th className="border-r border-black p-1 w-20">တရားရုံး<br/>အမည်</th>
                {/* အယူခံ / ပြင်ဆင် */}
                <th className="border-r border-black p-1 w-14">၁။အယူခံ<br/>၂။ပြင်ဆင်</th>
                <th className="border-r border-black p-1 w-16">တင်သွင်းသည့်<br/>ရက်စွဲ</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && !isPlaceholderData ? (
                <tr>
                  <td colSpan={15} className="text-center py-12 text-xs text-slate-400">
                    အချက်အလက်များ ဆွဲယူနေပါသည်...
                  </td>
                </tr>
              ) : isError ? (
                <tr>
                  <td colSpan={15} className="text-center py-12 text-xs text-red-500">
                    {error instanceof Error ? error.message : 'အချက်အလက်များ ဆွဲယူရာတွင် အမှားအယွင်းရှိခဲ့ပါသည်။'}
                  </td>
                </tr>
              ) : applications.length > 0 ? (
                applications.map((app: any, index: number) => {
                  const status = app.status || app.document?.status || 'DRAFT';
                  const formDetails = app.pLOFormFive || app.ploFormFive || app.formData || app;

                  return (
                    <tr key={app.id || app._id} className="border-b border-gray-400 hover:bg-slate-50 text-center align-middle">
                      {/* စဉ် */}
                      <td className="border-r border-black p-1 font-bold">
                        {(filters.currentPage - 1) * itemsPerPage + index + 1}
                      </td>

                      {/* ကျား / မ */}
                      <td className="border-r border-black p-1">
                        {formDetails.gender || '-'}
                      </td>

                      {/* သက်သေ၏အသက် */}
                      <td className="border-r border-black p-1">
                        {formDetails.victimAge || '-'}
                      </td>

                      {/* နစ်နာသူ / သက်သေအမည် */}
                      <td className="border-r border-black p-1 px-1">
                        {formDetails.victimOrWitnessName || '-'}
                      </td>

                      {/* သက်သေကြားနာမည့်နေ့ */}
                      <td className="border-r border-black p-1">
                        {formDetails.interviewDate || '-'}
                      </td>

                      {/* PTI ပထမအကြိမ် */}
                      <td className="border-r border-black p-1">
                        {formDetails.ptiFirstDate || '-'}
                      </td>

                      {/* PTI ဒုတိယအကြိမ် */}
                      <td className="border-r border-black p-1">
                        {formDetails.ptiSecondDate || '-'}
                      </td>

                      {/* တွေ့ဆုံမေးမြန်းသူအမည် */}
                      <td className="border-r border-black p-1 px-1">
                        {formDetails.interviewerName || '-'}
                      </td>

                      {/* တွေ့ဆုံမေးမြန်းသူရာထူး */}
                      <td className="border-r border-black p-1">
                        {formDetails.interviewerRole || '-'}
                      </td>

                      {/* စီရင်ချက်အမိန့် */}
                      <td className="border-r border-black p-1">
                        {formDetails.judgmentType || '-'}
                      </td>

                      {/* အမိန့်ချသည့်ရက်စွဲ */}
                      <td className="border-r border-black p-1">
                        {formDetails.judgmentDate || '-'}
                      </td>

                      {/* တရားရုံးအမည် */}
                      <td className="border-r border-black p-1 px-1">
                        {formDetails.courtName || '-'}
                      </td>

                      {/* အယူခံ / ပြင်ဆင် */}
                      <td className="border-r border-black p-1">
                        {formDetails.appealOrRevision || '-'}
                      </td>

                      {/* တင်သွင်းသည့်ရက်စွဲ */}
                      <td className="border-r border-black p-1">
                        {formDetails.submissionDate || '-'}
                      </td>

                      {/* မှတ်ချက် */}
                      <td className="border-r border-black p-1 px-1 text-left">
                        {formDetails.remarks || '-'}
                      </td>

                      {/* Action Buttons */}
                      <td className="p-1 font-sans text-center">
                        {(() => {
                          const isOwner = checkIsOriginUser(app);

                          return (
                            <div className="flex items-center justify-center gap-1">
                              {/* Only show Edit/Delete if status is DRAFT and user is the creator/origin office */}
                              {status === 'DRAFT' && isOwner && (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => navigate(`/plo-form-five/edit/${app.documentNumber}`)}
                                    className="inline-flex items-center gap-0.5 rounded bg-blue-50 px-1.5 py-0.5 text-[10px] font-semibold text-blue-600 hover:bg-blue-100 transition-colors"
                                    title="ပြင်ဆင်ရန်"
                                  >
                                    ✏️ Edit
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleDelete(app.documentNumber)}
                                    className="rounded p-0.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600 transition-colors"
                                    title="ဖျက်ရန်"
                                  >
                                    🗑️
                                  </button>
                                </>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={15} className="text-center py-12 text-xs text-slate-400">
                    ကိုက်ညီသော အချက်အလက် ရှာမတွေ့ပါ။
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!isLoading && totalPages > 1 && (
          <TablePagination
            currentPage={filters.currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={filters.setCurrentPage}
          />
        )}
      </div>
    </div>
  );
}